import { SectionShell } from "@/components/design-system/SectionShell";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CircleHelp } from "lucide-react";

const code = `import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CircleHelp } from "lucide-react";

<TooltipProvider delayDuration={200}>
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" aria-label="Help">
        <CircleHelp className="h-4 w-4" />
      </button>
    </TooltipTrigger>
    <TooltipContent>Short supporting explanation.</TooltipContent>
  </Tooltip>
</TooltipProvider>`;

export function TooltipSection() {
  return (
    <SectionShell
      id="tooltip"
      title="Tooltip"
      description={
        <>
          Built on Radix Tooltip. Opens on hover and on keyboard focus, closes
          on Escape. Content can be rich (lists, bold text) but should stay
          short. Wrap a region in <code>&lt;TooltipProvider&gt;</code> once.
        </>
      }
      whenToUse={
        <ul>
          <li>Brief help for a control or icon (a "?" next to a label).</li>
          <li>Explaining abbreviations or values on a button.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Essential information — put it on the page.</li>
          <li>Interactive content (links, buttons) — use a dialog or dropdown.</li>
          <li>Long explanations — link to a help page.</li>
        </ul>
      }
      preview={
        <TooltipProvider delayDuration={200}>
          <div className="flex flex-wrap items-center gap-6">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Help"
                  className="rounded-full text-ink-muted hover:text-ink-display"
                >
                  <CircleHelp className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>A short explanation of this control.</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="secondary">Hover or focus me</Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Tooltip on a button.</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li><code>side</code> on <code>&lt;TooltipContent&gt;</code>: <code>top</code> (default) | <code>bottom</code> | <code>left</code> | <code>right</code></li>
          <li>Use <code>asChild</code> on <code>&lt;TooltipTrigger&gt;</code> so the trigger is your own button.</li>
          <li>Icon-only triggers need an <code>aria-label</code>.</li>
          <li>Max width is <code>max-w-sm</code>; override with <code>className</code> if needed.</li>
        </ul>
      }
    />
  );
}
